import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/reportage";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const reportage: CaseStudy = {
  slug: "reportage",
  title: "Reportage Gallery",
  cardTitle: "Разработка сайта для галереи современного искусства Reportage",
  subtitle: "Галерея авторской фотографии",
  industry: { label: "Медиа и личный бренд", slug: "media" },
  eyebrow: "Сайт · Современное искусство",
  h1: "Разработка сайта для галереи Reportage",
  lead:
    "Разработали сайт цифровой галереи авторской фотографии Reportage: каталог работ с фильтрацией, страницы авторов, оформление изделий, избранное, корзину и оформление покупки.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт галереи" },

  seo: {
    primaryKeyword: "разработка сайта для галереи современного искусства",
    secondaryKeywords: [
      "создание сайта художественной галереи",
      "интернет-магазин искусства разработка",
      "сайт для арт-галереи",
      "сайт для продажи авторской фотографии",
    ],
    semanticEntities: [
      "каталог работ",
      "авторская фотография",
      "страница автора",
      "оформление работ",
      "тиражность",
      "сертификат подлинности",
      "избранное",
      "корзина",
    ],
    excludedKeywords: [
      "купить фотографию",
      "фото для интерьера",
      "современное искусство купить",
      "постеры",
    ],
    title: "Сайт Reportage Gallery — кейс HAY.agency",
    description:
      "Кейс HAY.agency: цифровая галерея авторской фотографии с каталогом, страницами авторов, оформлением работ и онлайн-покупкой.",
    h1: "Разработка сайта для галереи Reportage",
    slug: "reportage",
    canonicalUrl: `${SITE_URL}/cases/reportage`,
    ogTitle: "Сайт галереи авторской фотографии Reportage",
    ogDescription:
      "Каталог работ, страницы авторов, оформление, избранное и корзина для галереи современного искусства.",
    ogImage: `${SITE_URL}${dir}/reportage-og.webp`,
    robotsIndex: true,
    datePublished: "2024-02-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и корзина",
    },
    relatedIndustry: {
      label: "Разработка сайтов для медиа и личного бренда",
      to: routes.sites,
      note: "Editorial-подача и авторский контент",
    },
    relatedCases: [
      {
        label: "Разработка интернет-магазина ювелирного бренда GODSFORGE",
        to: "/cases/godsforge",
        note: "Премиальная предметная подача товара",
      },
      {
        label: "Интернет-магазин цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и корзина",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить сайт галереи",
  },

  client: {
    name: "Reportage Gallery",
    url: "https://reportage.gallery/",
    city: "Москва, ARTPLAY",
    industry: "Современное искусство",
  },
  year: "2024",
  services: [
    "Дизайн сайта галереи",
    "Разработка каталога работ",
    "Страницы авторов",
    "Карточка работы и оформление",
    "Избранное, корзина и оформление покупки",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Reportage Gallery" },
    { label: "Отрасль", value: "Современное искусство" },
    { label: "Тип проекта", value: "Сайт галереи авторской фотографии" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог работ с фильтрацией и страницей автора",
      "Карточка работы с размером, оформлением и вариантами рамы",
      "Раздел подлинности и тиражности изделий",
      "Избранное, корзина и оформление покупки",
      "Разделы сотрудничества, доставки и оплаты",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Reportage — галерея в ARTPLAY, которая продаёт тиражную авторскую фотографию для коллекций и интерьеров. Сайту нужно было выдержать editorial-подачу art-book и одновременно работать как полноценный интернет-магазин с корзиной и оформлением заказа.",
        "Отдельная задача — показать тиражность и подлинность каждой работы: покупатель должен понимать статус изделия ещё до оформления заказа.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: editorial-подача art-book вместо витрины маркетплейса",
    paragraphs: [
      "Интерфейс собран на тёплом сером фоне, крупная фотография остаётся главным элементом каждого экрана — сайт читается как арт-каталог, а не типовой интернет-магазин.",
      "Карточка работы и страница автора выстроены вокруг одного изображения без лишнего интерфейсного шума.",
    ],
    bullets: [
      "Тёплый серый фон вместо чистого белого",
      "Крупная фотография как главный элемент композиции",
      "Минимум интерфейсных элементов на странице работы",
    ],
  },

  identity: {
    fonts: [
      { name: "Geometria", role: "Заголовки", sample: "Reportage Gallery" },
      { name: "Golos Text", role: "Интерфейс и подписи", sample: "Каталог работ" },
    ],
    colors: [
      { hex: "#EDEAE4", name: "Фон витрины" },
      { hex: "#3A362F", name: "Текст" },
      { hex: "#9C8C74", name: "Акцент" },
    ],
  },

  cover: img(
    "reportage-case-cover",
    "Главная страница сайта галереи авторской фотографии Reportage",
    6,
  ),

  gallery: [
    img("reportage-catalog-author", "Каталог работ и страница автора на сайте Reportage", 6),
    img("reportage-product-frame", "Карточка работы с вариантами оформления Reportage", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия построена как вертикальная галерея: крупное изображение работы и минимум интерфейса на экране.",
      "Корзина и избранное на мобильных экранах показаны заполненными состояниями, как в реальном сценарии покупки.",
    ],
    images: [
      mobile("reportage-mobile-gallery", "Мобильная галерея работ на сайте Reportage"),
      mobile("reportage-mobile-cart", "Мобильная корзина на сайте Reportage"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Каталог и авторы",
      paragraphs: [
        "Каталог работ с фильтрацией ведёт к странице конкретного автора — покупатель может выбирать и по сюжету работы, и по имени фотографа.",
      ],
      bullets: ["Фильтрация каталога", "Страница автора", "Переход от работы к автору и обратно"],
    },
    {
      id: "product",
      title: "Карточка работы",
      paragraphs: [
        "Карточка показывает размер, вариант оформления, автора и статус подлинности — только актуальную механику покупки без лишних опций.",
      ],
      bullets: ["Размер и вариант оформления", "Рама или пластификация", "Автор и сертификат подлинности"],
    },
    {
      id: "purchase",
      title: "Покупка искусства",
      paragraphs: [
        "Избранное, корзина и оформление заказа собраны в стандартный e-commerce сценарий с заполненными состояниями.",
      ],
      bullets: ["Избранное", "Корзина", "Оформление заказа"],
    },
    {
      id: "mobile-gallery",
      title: "Mobile gallery",
      paragraphs: [
        "На мобильном экране подача вертикальная: крупное изображение работы и минимум интерфейсных элементов вокруг него.",
      ],
      bullets: ["Вертикальная лента работ", "Крупное изображение", "Минимум UI"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка сайта галереи", to: routes.ecom },
      { label: "Дизайн каталога и карточки работы", to: routes.ecom },
    ],
  },
};
